import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { loggerId, type LoggerId } from './Logger.js'
import {
  serviceContractItemId,
  type ServiceContractItemId,
} from './ServiceContractItem.js'
import { serviceItemId, type ServiceItemId } from './ServiceItem.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.logger_activation_ */
export type LoggerActivationId = string & { __brand: 'LoggerActivationId' }

/** Represents the table public.logger_activation_ */
export default interface LoggerActivationTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    LoggerActivationId,
    LoggerActivationId | undefined,
    LoggerActivationId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  loggerId: ColumnType<LoggerId, LoggerId, LoggerId>

  /** Database type: pg_catalog.uuid */
  serviceContractItemId: ColumnType<
    ServiceContractItemId | null,
    ServiceContractItemId | null,
    ServiceContractItemId | null
  >

  /** Database type: pg_catalog.timestamptz */
  activatedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  suspendedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.jsonb */
  cloudloopActivationResponse: ColumnType<
    unknown | null,
    unknown | null,
    unknown | null
  >

  /** Database type: pg_catalog.jsonb */
  cloudloopSuspensionResponse: ColumnType<
    unknown | null,
    unknown | null,
    unknown | null
  >

  /** Database type: pg_catalog.uuid */
  activatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  suspendedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  serviceItemId: ColumnType<
    ServiceItemId | null,
    ServiceItemId | null,
    ServiceItemId | null
  >
}

export type LoggerActivation = Selectable<LoggerActivationTable>

export type LoggerActivationInitializer = Insertable<LoggerActivationTable>

export type LoggerActivationMutator = Updateable<LoggerActivationTable>

export const loggerActivationId =
  z.string() as unknown as z.Schema<LoggerActivationId>

export const loggerActivation = z.object({
  id: loggerActivationId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  loggerId: loggerId,
  serviceContractItemId: serviceContractItemId.nullable().optional(),
  activatedAt: z.coerce.date(),
  suspendedAt: z.coerce.date().nullable().optional(),
  cloudloopActivationResponse: z.unknown().nullable().optional(),
  cloudloopSuspensionResponse: z.unknown().nullable().optional(),
  activatedBy: userId.nullable().optional(),
  suspendedBy: userId.nullable().optional(),
  serviceItemId: serviceItemId.nullable().optional(),
}) as unknown as z.Schema<LoggerActivation>

export const loggerActivationInitializer = z.object({
  id: loggerActivationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  loggerId: loggerId,
  serviceContractItemId: serviceContractItemId.optional().nullable(),
  activatedAt: z.coerce.date(),
  suspendedAt: z.coerce.date().optional().nullable(),
  cloudloopActivationResponse: z.unknown().optional().nullable(),
  cloudloopSuspensionResponse: z.unknown().optional().nullable(),
  activatedBy: userId.optional().nullable(),
  suspendedBy: userId.optional().nullable(),
  serviceItemId: serviceItemId.optional().nullable(),
}) as unknown as z.Schema<LoggerActivationInitializer>

export const loggerActivationMutator = z.object({
  id: loggerActivationId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  loggerId: loggerId.optional(),
  serviceContractItemId: serviceContractItemId.optional().nullable(),
  activatedAt: z.coerce.date().optional(),
  suspendedAt: z.coerce.date().optional().nullable(),
  cloudloopActivationResponse: z.unknown().optional().nullable(),
  cloudloopSuspensionResponse: z.unknown().optional().nullable(),
  activatedBy: userId.optional().nullable(),
  suspendedBy: userId.optional().nullable(),
  serviceItemId: serviceItemId.optional().nullable(),
}) as unknown as z.Schema<LoggerActivationMutator>
